import React, { Component, ComponentType, ErrorInfo } from "react";

import { logError } from "Utils/logger";
import { routerUrl } from "Utils/routerUrl";

import { Body2 } from "Cobalt/BodyText";
import { Callout, type CalloutProps } from "Cobalt/Callout";

import type { SerializedError } from "@reduxjs/toolkit";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";

export interface GenericErrorHandlerProps {
  className?: string;
  /* The name of the controller/flow in question */
  name: string;
  /** Error title to show */
  title?: string;
  /** Type of callout (warning is good for expected issues) */
  type?: CalloutProps["type"];
  children?: JSX.Element;
  component?: ComponentType<ErrorHandlerProps>;
  /** Hide children if an error occurs. Only use if errors are rendered elsewhere */
  hideOnError?: boolean;
  /** Additional props to provide as context for Honeybadger error */
  [errorContext: string]: any;
}

export interface RTKErrorHandlerProps extends GenericErrorHandlerProps {
  /* An action associated with the name of this component */
  action: string;
  isError: boolean;
  error: FetchBaseQueryError | SerializedError;
}

export type ErrorHandlerProps = GenericErrorHandlerProps | RTKErrorHandlerProps;

interface ErrorHandlerState {
  hasError: boolean;
  error?: FetchBaseQueryError | SerializedError;
}

/**
 * ErrorHandler: A higher order component that will catch and render errors at a granular level
 * Two primary use cases:
 *  1. Catch and render unexpected frontend errors (uses react error boundaries)
 *  2. Catch and render expected backend errors (uses isError + error from a redux toolkit query)
 *
 * Please check in with design as needed as we don't have a standard design yet.
 *
 * @see https://legacy.reactjs.org/docs/error-boundaries.html
 * (please note - look at the old docs because we use react 17)
 * */
export class ErrorHandler extends Component<ErrorHandlerProps, ErrorHandlerState> {
  state: ErrorHandlerState = { hasError: false };

  static getDerivedStateFromError(error: Error): ErrorHandlerState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  // Log an error automatically if child components fail to render
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { name = "ErrorHandler" } = this.props;
    const action = "render"; // this is specifically for render errors
    logError(
      error,
      { component: name, action },
      {
        ...this.extractContextFromProps(),
        ...errorInfo,
        // to my surprise, api does not accept arrays or hashes
        tags: "ErrorHandler,componentDidCatch",
      },
    );
  }

  // Log an error if a new "error" prop is provided
  componentDidUpdate(prevProps: ErrorHandlerProps) {
    const { name = "ErrorHandler", action = "componentDidUpdate", error, isError } = this.props;
    if (!this.state.hasError && isError && error && error !== prevProps.error) {
      // Try extracting serialized errors from RTK if provided
      const parsedError = new Error(error?.data?.error || error?.error || error);
      logError(
        parsedError,
        { component: name, action },
        {
          ...this.extractContextFromProps(),
          tags: "ErrorHandler,componentDidUpdate",
        },
      );
    }
  }

  extractContextFromProps(): Record<string, any> {
    const { name, action, isError, title, type, error, children, component, hideOnError, ...contextProps } = this.props;
    return contextProps;
  }

  render(): JSX.Element {
    const { isError, error, hideOnError, children, title = "Something went wrong", component: ErrorComponent, mt = 4, className } = this.props;
    const { hasError } = this.state;

    if (!hasError && !isError) {
      return children || null;
    }

    if (hideOnError) return null;

    if (ErrorComponent) {
      return <ErrorComponent mt={mt} className={className} {...this.props} />;
    }

    const displayError = hasError ? this.state.error : error;
    const type = hasError ? "error" : this.props.type || "error";
    const errorMessage =
      typeof displayError?.data?.error === "function" ? displayError.data.error() : displayError?.data?.error || "An unexpected error has occurred.";

    return (
      <Callout
        className={className}
        type={type}
        title={title}
        body={
          <>
            <Body2>{errorMessage}</Body2>
            {type === "error" && (
              <a href={`${routerUrl()}/contact`}>
                <Body2 underlined color="error" mt={3}>
                  Contact Support
                </Body2>
              </a>
            )}
          </>
        }
        mt={mt}
        style={{ maxWidth: "unset" }}
      />
    );
  }
}

/**
 * A higher-order component that wraps a given component with error handling using the ErrorHandler component.
 */
export function withErrorHandler<T extends ErrorHandlerProps>(Component: ComponentType<T>, options: Partial<GenericErrorHandlerProps> = {}): ComponentType<T> {
  const displayName = Component.displayName || Component.name || "Component";

  const WithErrorHandler = (props: T) => (
    <ErrorHandler name={displayName} {...props} {...options}>
      <Component {...props} />
    </ErrorHandler>
  );

  // Setting the display name for better debugging
  WithErrorHandler.displayName = `withErrorHandler(${displayName})`;

  return WithErrorHandler;
}
